/**
 * Corporate-audience helpers for admin dashboard list/edit.
 * Prefer API fields (`isCorporateAssigned`, `organizations[].isCorporate`,
 * `organizations[].hasCorporateUsers`, `users[].user_type`) — do not hardcode org names.
 */

/** @typedef {'public' | 'standard' | 'corporate' | 'mixed'} DashboardAudience */

/**
 * @param {unknown} value
 * @returns {DashboardAudience | null}
 */
export function normalizeDashboardAudience(value) {
  if (value === "public" || value === "standard" || value === "corporate" || value === "mixed") {
    return value;
  }
  return null;
}

/** @param {unknown} userType */
export function isCorporateUserType(userType) {
  return String(userType || "").toLowerCase() === "corporates";
}

/**
 * Org counts as corporate for UI when:
 * - API marks `isCorporate`, OR
 * - API marks `hasCorporateUsers` (org has any corporates user), OR
 * - picker shows at least one corporate user in that org (edit-time fallback).
 *
 * @param {{
 *   isCorporate?: boolean,
 *   hasCorporateUsers?: boolean,
 *   organization_id?: number,
 *   id?: number,
 * } | null | undefined} org
 * @param {Array<{ organizationId?: number, userType?: string | null }>} [pickerUsers]
 */
export function isCorporateOrganization(org, pickerUsers) {
  if (!org) return false;
  if (org.isCorporate === true || org.isCorporate === "true") return true;
  if (org.hasCorporateUsers === true || org.hasCorporateUsers === "true") return true;

  const orgId = Number(org.organization_id ?? org.id);
  if (Number.isNaN(orgId) || !Array.isArray(pickerUsers) || pickerUsers.length === 0) {
    return false;
  }
  return pickerUsers.some(
    (user) => Number(user.organizationId) === orgId && isCorporateUserType(user.userType)
  );
}

/**
 * True when selected org id has corporate members (API meta and/or picker).
 *
 * @param {number|string} orgId
 * @param {Array<{ organization_id?: number, id?: number, isCorporate?: boolean, hasCorporateUsers?: boolean }>} [organizationsMeta]
 * @param {Array<{ organizationId?: number, userType?: string | null }>} [pickerUsers]
 */
export function orgIdHasCorporateUsers(orgId, organizationsMeta, pickerUsers) {
  const id = Number(orgId);
  if (Number.isNaN(id)) return false;

  const meta = (organizationsMeta || []).find(
    (org) => Number(org.organization_id ?? org.id) === id
  );
  if (isCorporateOrganization(meta, pickerUsers)) return true;

  return (pickerUsers || []).some(
    (user) => Number(user.organizationId) === id && isCorporateUserType(user.userType)
  );
}

/**
 * True when the dashboard has any corporate assignee (pure corporate or mixed).
 * Prefer API `isCorporateAssigned`; also honor org `hasCorporateUsers` when present.
 *
 * @param {{
 *   isCorporateAssigned?: boolean,
 *   audience?: unknown,
 *   corporateAssigneeCount?: number,
 *   organizations?: Array<{ isCorporate?: boolean, hasCorporateUsers?: boolean }>,
 * }} dashboard
 */
export function isCorporateAssociatedDashboard(dashboard) {
  if (
    dashboard?.isCorporateAssigned === true ||
    dashboard?.isCorporateAssigned === "true"
  ) {
    return true;
  }
  if (typeof dashboard?.corporateAssigneeCount === "number" && dashboard.corporateAssigneeCount > 0) {
    return true;
  }
  const orgs = Array.isArray(dashboard?.organizations) ? dashboard.organizations : [];
  if (
    orgs.some(
      (org) =>
        org?.isCorporate === true ||
        org?.isCorporate === "true" ||
        org?.hasCorporateUsers === true ||
        org?.hasCorporateUsers === "true"
    )
  ) {
    return true;
  }
  const audience = normalizeDashboardAudience(dashboard?.audience);
  return audience === "corporate" || audience === "mixed";
}

/**
 * @param {{
 *   isCorporateAssigned?: boolean,
 *   organizations?: Array<{ organization_id?: number, id?: number, isCorporate?: boolean, hasCorporateUsers?: boolean }>,
 *   users?: Array<{ user_id?: number, user_type?: string }>,
 *   organizationIds?: number[],
 *   userIds?: number[],
 *   pickerUsers?: Array<{ userIndex: number, organizationId?: number, userType?: string | null }>,
 * }} input
 */
export function resolveIsCorporateAssigned({
  isCorporateAssigned,
  organizations,
  users,
  organizationIds,
  userIds,
  pickerUsers,
} = {}) {
  if (isCorporateAssigned === true) return true;

  const selectedOrgIds = (organizationIds || [])
    .map(Number)
    .filter((n) => !Number.isNaN(n));

  if (
    selectedOrgIds.some((orgId) =>
      orgIdHasCorporateUsers(orgId, organizations, pickerUsers)
    )
  ) {
    return true;
  }

  const orgList = Array.isArray(organizations) ? organizations : [];
  if (
    selectedOrgIds.length === 0 &&
    orgList.some((org) => isCorporateOrganization(org, pickerUsers))
  ) {
    return true;
  }

  const selectedUserIds = new Set((userIds || []).map(Number).filter((n) => !Number.isNaN(n)));
  const userList = Array.isArray(users) ? users : [];
  if (
    userList.some((user) => {
      if (!isCorporateUserType(user?.user_type)) return false;
      if (selectedUserIds.size === 0) return true;
      const id = Number(user.user_id);
      return !Number.isNaN(id) && selectedUserIds.has(id);
    })
  ) {
    return true;
  }

  const picker = Array.isArray(pickerUsers) ? pickerUsers : [];
  if (
    picker.some((user) => {
      if (!isCorporateUserType(user?.userType)) return false;
      const id = Number(user.userIndex);
      return !Number.isNaN(id) && selectedUserIds.has(id);
    })
  ) {
    return true;
  }

  return false;
}
